<template>
  <v-text-field
    :model-value="localValue"
    type="number"
    inputmode="decimal"
    :rules="wrappedRules"
    @update:model-value="onUpdate"
    @wheel="onWheel"
  />
</template>

<script setup>
// Drop-in replacement for `<v-text-field type="number">`, used for every
// numeric input in the app. Fixes two issues that turned out to be common
// across almost every numeric field in the system:
//   1. Vuetify's v-model.number keeps the *raw string* whenever it can't run
//      it through parseFloat - which includes an emptied field (parseFloat
//      of '' is NaN, so v-model.number leaves the bound value as the literal
//      string ''). Screens that then built their save payload with
//      `value || null` turned a legitimate 0 into null before it ever left
//      the client (so "save 0" silently became "clear the field" and looked
//      like it reverted on reopen); screens that forwarded the field as-is
//      instead sent the literal string '' straight to a decimal? endpoint,
//      which the backend rejected outright ("save empty" failed with an
//      error). This component always emits either a real Number or null -
//      never a string - so both cases behave like clearing any other field.
//   2. Scrolling the mouse wheel over a focused number input silently
//      increments/decrements it in Chrome/Firefox. This blurs the field on
//      wheel so a page scroll never doubles as an edit.
// Every other v-text-field prop (label, hint, density, suffix, min, max,
// step, ...) passes straight through via Vue's automatic attribute
// fallthrough - only modelValue and rules are intercepted here.
// `rules` needs interception too: the inner v-text-field validates against
// `localValue`, which is always a string (an empty field is '', never
// null) - so a caller's rule like `v => v !== null || 'required'` never
// actually saw null and always passed, letting an empty required field
// through validation. wrappedRules re-parses the string back to the same
// Number/null shape the caller's v-model receives before invoking their
// rule, so rules see the real value instead of the internal string proxy.
import { ref, computed, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: null
  },
  rules: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue']);

const toLocal = (val) => (val === null || val === undefined ? '' : String(val));
const localValue = ref(toLocal(props.modelValue));

const parseForRules = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
};
const wrappedRules = computed(() => props.rules.map((rule) => (raw) => rule(parseForRules(raw))));

// Re-sync from external changes (e.g. a dialog resetting its form) without
// fighting the user mid-keystroke: only overwrite the local text if it no
// longer represents the same number, so typing "12." while the parent still
// holds 12 doesn't get stomped back to "12".
watch(
  () => props.modelValue,
  (val) => {
    const parsedLocal = localValue.value === '' ? null : Number(localValue.value);
    if (val !== parsedLocal) {
      localValue.value = toLocal(val);
    }
  }
);

const onUpdate = (val) => {
  localValue.value = val;
  if (val === '' || val === null || val === undefined) {
    emit('update:modelValue', null);
    return;
  }
  const num = Number(val);
  emit('update:modelValue', Number.isNaN(num) ? null : num);
};

const onWheel = (event) => {
  event.target.blur();
};
</script>
